/* global $, Bloodhound */
// jQuery plugin - loaded via jQueryPluginsLoader Vite plugin (see vite.config.js)
import 'corejs-typeahead/dist/typeahead.bundle';
import { Text, withText } from 'preact-i18n';
import { Component, Fragment } from 'preact';
import { camelizeKeys } from 'humps';
import { placeTemplate, selectIcon } from '../templates/place';
import notFoundTemplate from '../templates/not-found';
import pendingTemplate from '../templates/pending';
import recentTripTemplate from '../templates/recentTrip';
import createUrl from '../utils/createUrl';
import filterBySomeValues from '../utils/filterBySomeValues';

function getDisplay(place) {
  const { resultType, cityName, state, display } = place;

  if (resultType === 'city') {
    return `${cityName}, ${state}`;
  }
  return `${cityName}, ${display}`;
}

class AutocompleteTypeahead extends Component {
  constructor(props) {
    super(props);

    this.filterCitiesByCountry = this.filterCitiesByCountry.bind(this);
  }

  componentDidMount() {
    const { field, initialPlaceSlug } = this.props;

    this.$input = $(this.fieldWrapperEl).find(`input.${field}`);
    this.placeBloodhound = this.createPlaceBloodhound();
    // NOTE: The following line is a hack that prevents preact double rendering
    // DELETE me and the display:none explicit style on render method
    this.$input.show();
    this.setupTypeahead();
    this.setupEvents();

    if (!initialPlaceSlug) return;

    this.setAutocompleteValue(initialPlaceSlug);
  }

  componentDidUpdate(prevProps) {
    const { place: prevPlace, placeholder: prevPlaceholder } = prevProps;
    const { place, placeholder } = this.props;

    if (place !== prevPlace) {
      this.refreshTextInput();
    }

    if (placeholder !== prevPlaceholder) {
      this.$input.attr('placeholder', placeholder);
    }
  }

  setAutocompleteValue(placeSlug) {
    const { field, onChange } = this.props;

    this.fetchPlace(placeSlug).then(response => {
      const fetchedPlace = camelizeKeys(response.find(place => place.slug === placeSlug));

      if (fetchedPlace) {
        const { display } = fetchedPlace;
        this.$input.typeahead('val', display);
        onChange(field, fetchedPlace);
      }
    });
  }

  setupEvents() {
    const { field, onChange, onInputChange } = this.props;

    this.$input.on('typeahead:selected', ($e, datum) => {
      onChange(field, datum);
    });

    this.$input.on('click', () => this.$input.select());

    this.$input.on('blur', () => this.refreshTextInput());

    this.$input.on('input', () => {
      const value = this.$input.val();
      onInputChange(field, value);
    });
  }

  setupTypeahead() {
    const {
      field,
      isHotelSearch,
      noResultsLabel,
      allTerminalsLabel,
      onNoSearchResults,
    } = this.props;

    this.$input.typeahead(
      {
        hint: false,
        highlight: true,
        autoselect: true,
        minLength: 0,
      },
      {
        name: 'trips',
        displayKey: datum => {
          const place = field in datum ? datum[field] : datum;
          return getDisplay(place);
        },
        limit: 3,
        source: this.createRecentTripsSource(),
        templates: {
          suggestion: recentTripTemplate,
        },
      },
      {
        name: 'city',
        displayKey: getDisplay,
        limit: 10,
        source: (query, syncCallback, asyncCallback) => {
          if (query.length > 0) {
            this.placeBloodhound.search(query, syncCallback, asyncCallback);
            return;
          }
          syncCallback();
          asyncCallback();
        },
        templates: {
          suggestion: datum => placeTemplate(datum, allTerminalsLabel, isHotelSearch),
          notFound: ({ query }) => {
            if (query && query.length > 0) {
              onNoSearchResults(field, query);
            }
            return notFoundTemplate(query, noResultsLabel);
          },
          pending: pendingTemplate,
        },
      }
    );
  }

  queriedUrl = (sourceUrl, query) => {
    const { from, to } = this.props;
    const url = createUrl(sourceUrl);

    if (query) {
      url.setQueryParam('q', query);
    }

    if (from) {
      url.setQueryParam('from', from);
    } else if (to) {
      url.setQueryParam('to', to);
    }

    return url.href;
  };

  filterCitiesByCountry(response) {
    const { country } = this.props;
    let places = [];

    if (!country) return camelizeKeys(response);

    if (country === 'mexico') {
      places = response.filter(place => place.result_type === 'city' && place.country === 'México');
    }

    return camelizeKeys(places);
  }

  fetchPlace(place) {
    const { sourceUrl } = this.props;
    const placesUrl = this.queriedUrl(sourceUrl, place);
    return new Promise(resolve => {
      $.get(placesUrl, data => resolve(data), 'json');
    });
  }

  createPlaceBloodhound() {
    const { sourceUrl } = this.props;

    return new Bloodhound({
      datumTokenizer: Bloodhound.tokenizers.obj.whitespace('ascii_display'),
      queryTokenizer: Bloodhound.tokenizers.whitespace,
      identify: datum => datum.slug,
      remote: {
        url: sourceUrl,
        rateLimitWait: 300,
        replace: this.queriedUrl,
        transform: this.filterCitiesByCountry,
      },
      prefetch: {
        url: createUrl(sourceUrl).setQueryParam('prefetch', 'true').href,
        cache: true,
        transform: this.filterCitiesByCountry,
      },
    });
  }

  createRecentTripsSource() {
    return (query, callback) => {
      let { recentTrips = [] } = this.props;
      if (!recentTrips) recentTrips = [];
      if (query === '') callback(recentTrips);

      callback(
        recentTrips.filter(trip =>
          filterBySomeValues(
            query,
            trip.origin.cityName,
            trip.origin.display,
            trip.destination.cityName,
            trip.destination.display
          )
        )
      );
    };
  }

  refreshTextInput() {
    const { place } = this.props;

    if (place && place.slug) {
      const display = getDisplay(place);
      this.$input.val(display);
    } else {
      // hack for clear the field text
      this.$input.typeahead('val', '.').typeahead('val', '');
    }
  }

  render({ field, error, placeholder_context, onFocus, label }) {
    return (
      <div
        className={`${field}-wrapper`}
        key={`${field}-wrapper`}
        ref={el => (this.fieldWrapperEl = el)}
      >
        <Fragment>
          {error && (
            <div
              className="icon-error"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: selectIcon('error', false),
              }}
            />
          )}
          <label className="label label-route" for={placeholder_context}>
            <Text id={`label.${label}`} />
          </label>
          <input
            type="text"
            className={field}
            placeholder={placeholder_context}
            style="display:none !important"
            autoComplete="off"
            onFocus={onFocus}
          />
        </Fragment>
      </div>
    );
  }
}

AutocompleteTypeahead.defaultProps = {
  searchByCity: false,
};

export default withText(props => ({
  placeholder_context: <Text id={`placeholder.${props.placeholder}`} />,
  noResultsLabel: 'notification.no_results',
  allTerminalsLabel: 'placeholder.all_terminals',
}))(AutocompleteTypeahead);
